import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Newspaper,
  ChevronRight,
} from 'lucide-react';
import styles from './MySpace.module.css';
import { Modal } from '../../components/UI';

interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  thumbnail?: string;
  source: string;
  category?: string;
}

interface QuoteData {
  content: string;
  author: string;
}

type NewsCategory = 'all' | 'technology' | 'business' | 'sports';

const CATEGORY_EMOJI: Record<string, string> = {
  'All News': '🌐',
  'Business': '📈',
  'Technology': '💻',
  'Sports': '⚽',
};

const QUICK_LINKS = [
  { id: 'projects', label: 'Projects', desc: 'Access your project workspace', iconSrc: '/icons/notion.svg', gradient: 'linear-gradient(135deg, #6366F1, #818CF8)', shadow: 'rgba(99,102,241,0.2)', link: 'https://notion.so' },
  { id: 'benefits', label: 'Compensation & Benefits', desc: 'View payroll and benefits', iconSrc: '/icons/razorpay-payroll.svg', gradient: 'linear-gradient(135deg, #0A111A, #1C2431)', shadow: 'rgba(77,127,255,0.25)', link: 'https://payroll.razorpay.com' },
  { id: 'bodhi', label: 'Bodhi Tree', desc: 'Courses and certifications', iconSrc: '/icons/bodhi-tree.png', imgSize: 48, gradient: 'linear-gradient(135deg, #D97706, #FBBF24)', shadow: 'rgba(217,119,6,0.2)', link: '#' },
  { id: 'health', label: 'Health & Wellness', desc: 'Insurance and wellness services', iconSrc: '/icons/health-wellness.png', imgSize: 48, gradient: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)', shadow: 'rgba(16,185,129,0.15)', hasPopup: true },
];

const HEALTH_SUB_LINKS = [
  { id: 'insurance', label: 'Insurance', desc: 'Kotak Mahindra Health Insurance', iconSrc: '/icons/kmbl-logo.svg', gradient: 'linear-gradient(135deg, #EEF2FF, #E0E7FF)', shadow: 'rgba(99,102,241,0.12)', link: 'https://m.fhpl.net/' },
  { id: 'superhealth', label: 'SuperHealth', desc: 'Wellness services by SuperHealth', iconSrc: '/icons/superhealth.jpeg', imgSize: 44, gradient: 'linear-gradient(135deg, #F3E8FF, #EDE9FE)', shadow: 'rgba(139,92,246,0.12)', link: 'https://www.superhealth.co.in/auth/super/login' },
];

const MySpace: React.FC = () => {
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [allNews, setAllNews] = useState<NewsItem[]>([]);
  const [loadingQuote, setLoadingQuote] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [quoteError, setQuoteError] = useState('');
  const [newsError, setNewsError] = useState('');
  const [activeCategory, setActiveCategory] = useState<NewsCategory>('all');
  const [showHealthPopup, setShowHealthPopup] = useState(false);

  const newsCategories: { id: NewsCategory; label: string }[] = [
    { id: 'all', label: 'All News' },
    { id: 'business', label: 'Business' },
    { id: 'technology', label: 'Technology' },
    { id: 'sports', label: 'Sports' },
  ];

  const curatedQuotes: QuoteData[] = [
    { content: "Your most unhappy customers are your greatest source of learning.", author: "Bill Gates" },
    { content: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
    { content: "Risk more than others think is safe. Dream more than others think is practical.", author: "Howard Schultz" },
    { content: "The secret of business is to know something that nobody else knows.", author: "Aristotle Onassis" },
    { content: "Chase the vision, not the money; the money will end up following you.", author: "Tony Hsieh" },
    { content: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { content: "Innovation distinguishes between a leader and a follower.", author: "Steve Jobs" },
    { content: "Stay hungry, stay foolish.", author: "Steve Jobs" },
    { content: "Technology is best when it brings people together.", author: "Matt Mullenweg" },
    { content: "Any sufficiently advanced technology is indistinguishable from magic.", author: "Arthur C. Clarke" },
    { content: "A leader is one who knows the way, goes the way, and shows the way.", author: "John C. Maxwell" },
    { content: "Leadership is not about being in charge. It's about taking care of those in your charge.", author: "Simon Sinek" },
    { content: "You have the right to work, but never to the fruit of work.", author: "Bhagavad Gita" },
    { content: "Be the change that you wish to see in the world.", author: "Mahatma Gandhi" },
    { content: "Live as if you were to die tomorrow. Learn as if you were to live forever.", author: "Mahatma Gandhi" },
    { content: "Arise, awake and stop not till the goal is reached.", author: "Swami Vivekananda" },
    { content: "If you want to walk fast, walk alone. But if you want to walk far, walk together.", author: "Ratan Tata" },
    { content: "Dream, dream, dream. Dreams transform into thoughts and thoughts result in action.", author: "APJ Abdul Kalam" },
    { content: "Growth is painful. Change is painful. But nothing is as painful as staying stuck somewhere you don't belong.", author: "N. R. Narayana Murthy" },
  ];

  const fetchQuote = () => {
    setLoadingQuote(true);
    setQuoteError('');
    const randomQuote = curatedQuotes[Math.floor(Math.random() * curatedQuotes.length)];
    setQuote(randomQuote);
    setLoadingQuote(false);
  };

  const extractImageFromContent = (content: string): string | undefined => {
    if (!content) return undefined;
    const patterns = [
      /<img[^>]+src="([^">]+)"/i,
      /<img[^>]+src='([^'>]+)'/i,
      /src="(https?:\/\/[^"]+\.(jpg|jpeg|png|gif|webp)[^"]*)"/i,
    ];
    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match && match[1]) return match[1];
    }
    return undefined;
  };

  const getSourceGradient = (source: string): string => {
    const gradients: Record<string, string> = {
      'Livemint': 'linear-gradient(135deg, #ec7d10, #f5a623)',
      'Moneycontrol': 'linear-gradient(135deg, #4e2a8e, #7b4cc4)',
      'Economic Times': 'linear-gradient(135deg, #1a73e8, #4285f4)',
      'Business Standard': 'linear-gradient(135deg, #c62828, #ef5350)',
      'TechCrunch': 'linear-gradient(135deg, #0a9800, #4caf50)',
      'The Verge': 'linear-gradient(135deg, #fa4b2a, #ff7043)',
      'NDTV Sports': 'linear-gradient(135deg, #e53935, #ef5350)',
      'Times of India': 'linear-gradient(135deg, #d32f2f, #f44336)',
      'Hindustan Times': 'linear-gradient(135deg, #0072bc, #2196f3)',
      'India Today': 'linear-gradient(135deg, #e53935, #ef5350)',
      'NDTV Profit': 'linear-gradient(135deg, #e53935, #ef5350)',
    };
    return gradients[source] || 'linear-gradient(135deg, #667eea, #764ba2)';
  };

  const fetchNews = async () => {
    setLoadingNews(true);
    setNewsError('');
    const newsFeeds = [
      { url: 'https://techcrunch.com/feed/', source: 'TechCrunch', category: 'technology' },
      { url: 'https://www.theverge.com/rss/index.xml', source: 'The Verge', category: 'technology' },
      { url: 'https://economictimes.indiatimes.com/markets/rssfeeds/1977021501.cms', source: 'Economic Times', category: 'business' },
      { url: 'https://www.livemint.com/rss/markets', source: 'Livemint', category: 'business' },
      { url: 'https://www.moneycontrol.com/rss/latestnews.xml', source: 'Moneycontrol', category: 'business' },
      { url: 'https://feeds.feedburner.com/ndtvsports-latest', source: 'NDTV Sports', category: 'sports' },
      { url: 'https://timesofindia.indiatimes.com/rssfeeds/4719148.cms', source: 'Times of India', category: 'sports' },
    ];

    try {
      const fetchWithTimeout = async (url: string, timeout = 10000) => {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        try {
          const response = await fetch(url, { signal: controller.signal });
          clearTimeout(id);
          return response;
        } catch (error) {
          clearTimeout(id);
          throw error;
        }
      };

      const fetchPromises = newsFeeds.map(async (feed) => {
        try {
          const cacheBuster = new Date().getTime();
          const response = await fetchWithTimeout(
            `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feed.url)}&_=${cacheBuster}`
          );
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'ok' && data.items) {
              return data.items.slice(0, 5).map((item: any) => {
                let thumbnail = item.thumbnail ||
                  item.enclosure?.link ||
                  extractImageFromContent(item.content) ||
                  extractImageFromContent(item.description);
                if (thumbnail && (thumbnail.includes('undefined') || thumbnail.length < 10 || !thumbnail.startsWith('http'))) {
                  thumbnail = null;
                }
                return {
                  title: item.title,
                  link: item.link,
                  pubDate: item.pubDate,
                  description: item.description?.replace(/<[^>]*>/g, '').substring(0, 150) + '...',
                  thumbnail,
                  source: feed.source,
                  category: feed.category,
                };
              });
            }
          }
          return [];
        } catch {
          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const combinedNews = results.flat();
      combinedNews.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());

      if (combinedNews.length > 0) {
        setAllNews(combinedNews);
      } else {
        setNewsError('Unable to load news. Please try again later.');
      }
    } catch {
      setNewsError('Unable to load news. Please try again later.');
    } finally {
      setLoadingNews(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return 'JUST NOW';
    if (diffHours < 24) return `${diffHours}H AGO`;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 7) return `${diffDays}D AGO`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
  };

  const filteredNews = activeCategory === 'all'
    ? (['business', 'technology', 'sports'] as const).flatMap(cat =>
        allNews.filter(item => item.category === cat)
          .sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime())
          .slice(0, 4)
      )
    : allNews.filter((item: any) => item.category === activeCategory).slice(0, 10);

  useEffect(() => {
    fetchQuote();
    fetchNews();
    const quoteInterval = setInterval(fetchQuote, 30000);
    return () => clearInterval(quoteInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <h1>My space</h1>
        <p>Your corner of Ripple — a thought to start on, the tools you use daily, and what's moving outside.</p>
      </div>

      {/* ── Quote Banner ── */}
      <section className={styles.section}>
        <div className={styles.quoteBar}>
          <div className={styles.quoteInner}>
            <div className={styles.quoteContent}>
              <div className={styles.quoteLabel}>
                <span className={styles.quoteLabelDot} />
                <span className={styles.quoteLabelText}>TODAY'S THOUGHT</span>
              </div>
            {loadingQuote ? (
              <p className={styles.quoteText}>Loading...</p>
            ) : quote ? (
              <>
                <p className={styles.quoteText}>"{quote.content}"</p>
                <div className={styles.quoteAuthor}>{quote.author}</div>
              </>
            ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick Access ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0C6B62" strokeWidth="2.2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Quick Access
          </h2>
        </div>
        <div className={styles.quickLinksGrid}>
          {QUICK_LINKS.map((l) => {
            const cardContent = (
              <>
                <div
                  className={styles.quickLinkIcon}
                  style={{ background: l.gradient, boxShadow: `0 4px 12px ${l.shadow}` }}
                >
                  {l.iconSrc ? (
                    <img src={l.iconSrc} alt={l.label} className={styles.quickLinkImg} style={(l as any).imgSize ? { width: (l as any).imgSize, height: (l as any).imgSize } : undefined} />
                  ) : (
                    <span style={{ fontSize: 28 }}>{(l as any).icon}</span>
                  )}
                </div>
                <div className={styles.quickLinkInfo}>
                  <div className={styles.quickLinkTitle}>{l.label}</div>
                  <div className={styles.quickLinkDesc}>{l.desc}</div>
                </div>
              </>
            );

            if ((l as any).hasPopup) {
              return (
                <div
                  key={l.id}
                  className={styles.quickLinkCard}
                  onClick={() => setShowHealthPopup(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowHealthPopup(true); }}
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <a key={l.id} href={(l as any).link} target="_blank" rel="noopener noreferrer" className={styles.quickLinkCard}>
                {cardContent}
              </a>
            );
          })}
        </div>
      </section>

      {/* ── Latest News ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0C6B62" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
            </svg>
            Latest News
          </h2>
        </div>

        <div className={styles.categoryRibbon}>
          {newsCategories.map((c) => (
            <button
              key={c.id}
              className={`${styles.categoryBtn} ${activeCategory === c.id ? styles.active : ''}`}
              onClick={() => setActiveCategory(c.id)}
            >
              {CATEGORY_EMOJI[c.label] || '📰'} <span>{c.label}</span>
            </button>
          ))}
        </div>

        {loadingNews ? (
          <div className={styles.newsLoading}>
            <div className={styles.spinner} />
            <span>Loading latest news...</span>
          </div>
        ) : newsError ? (
          <div className={styles.newsError}>
            <Newspaper size={48} />
            <p>{newsError}</p>
            <button onClick={fetchNews} className={styles.retryBtn}>
              <RefreshCw size={16} /> Try Again
            </button>
          </div>
        ) : (
          <div className={styles.newsGrid}>
            {filteredNews.map((item: any, index) => (
              <a key={index} href={item.link} target="_blank" rel="noopener noreferrer" className={styles.newsCard}>
                <div className={styles.newsImageContainer} style={{ background: getSourceGradient(item.source) }}>
                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className={styles.newsImage}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className={styles.newsImagePlaceholder} style={{ background: getSourceGradient(item.source) }}>
                      <span style={{ color: 'white', fontSize: 40, fontWeight: 'bold', opacity: 0.8 }}>
                        {item.source.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span className={styles.sourceTag}>{item.source}</span>
                </div>
                <div className={styles.newsContent}>
                  <span className={styles.newsDate}>{formatDate(item.pubDate)}</span>
                  <h3 className={styles.newsTitle}>{item.title}</h3>
                  <p className={styles.newsDescription}>{item.description}</p>
                </div>
              </a>
            ))}
          </div>
        )}

        {!loadingNews && !newsError && filteredNews.length > 0 && (
          <div className={styles.viewMoreContainer}>
            <a
              href={activeCategory === 'technology' ? 'https://techcrunch.com/' :
                    activeCategory === 'business' ? 'https://economictimes.indiatimes.com/' :
                    activeCategory === 'sports' ? 'https://www.espn.in/' :
                    'https://news.google.com/?hl=en-IN&gl=IN&ceid=IN:en'}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewMoreBtn}
            >
              Explore More News <ChevronRight size={16} />
            </a>
          </div>
        )}
      </section>

      {/* Health & Wellness Popup */}
      <Modal isOpen={showHealthPopup} onClose={() => setShowHealthPopup(false)} title="Health & Wellness" size="sm">
        <div className={styles.healthSubLinks}>
          {HEALTH_SUB_LINKS.map((sub) => (
            <a
              key={sub.id}
              href={sub.link}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.healthSubCard}
              onClick={() => setShowHealthPopup(false)}
            >
              <div
                className={styles.healthSubIcon}
                style={{ background: sub.gradient, boxShadow: `0 4px 12px ${sub.shadow}` }}
              >
                <img src={sub.iconSrc} alt={sub.label} className={styles.healthSubImg} style={(sub as any).imgSize ? { width: (sub as any).imgSize, height: (sub as any).imgSize } : undefined} />
              </div>
              <div className={styles.healthSubInfo}>
                <div className={styles.healthSubTitle}>{sub.label}</div>
                <div className={styles.healthSubDesc}>{sub.desc}</div>
              </div>
            </a>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default MySpace;
